import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

/** Aligné sur `CandidatureModel` (app mobile) : texte libre + URLs des PDF. */
export interface Candidature {
  id: string;
  userId?: string;
  postId?: string;
  status?: string;
  date?: Date;
  description?: string;
  documents: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

function documentsFromFirestore(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => (typeof e === 'string' ? e.trim() : ''))
    .filter(Boolean);
}

const COLLECTION = 'candidatures';

export async function listCandidatures(): Promise<Candidature[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), orderBy('date', 'desc'))
  );
  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      userId: data.userId,
      postId: data.postId,
      status: data.status,
      date: data.date?.toDate ? data.date.toDate() : undefined,
      description:
        typeof data.description === 'string' ? data.description : undefined,
      documents: documentsFromFirestore(data.documents),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
    };
  });
}

export async function updateCandidatureStatus(
  id: string,
  status: 'approved' | 'rejected' | 'pending'
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, { status });
}

/** Mise à jour uniquement de la liste `documents` (ex. après suppression d’un fichier Storage). */
export async function updateCandidatureDocumentsOnly(
  id: string,
  documents: string[]
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    documents,
    updatedAt: new Date(),
  });
}

export async function updateCandidature(
  id: string,
  payload: {
    userId: string;
    postId: string;
    status: 'approved' | 'rejected' | 'pending';
    date: Date;
    description: string;
    documents: string[];
  }
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    userId: payload.userId.trim(),
    postId: payload.postId.trim(),
    status: payload.status,
    date: Timestamp.fromDate(payload.date),
    description: payload.description.trim() || null,
    documents: payload.documents,
    updatedAt: new Date(),
  });
}

export async function createCandidature(payload: {
  userId: string;
  postId: string;
  status: 'approved' | 'rejected' | 'pending';
  date: Date;
  description: string;
  documents: string[];
}): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    userId: payload.userId.trim(),
    postId: payload.postId.trim(),
    status: payload.status,
    date: Timestamp.fromDate(payload.date),
    description: payload.description.trim() || null,
    documents: payload.documents,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

